import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AyurvedaButton } from '@/components/ui/ayurveda-button';
import {
  AyurvedaCard,
  AyurvedaCardContent,
  AyurvedaCardHeader,
  AyurvedaCardTitle,
  AyurvedaCardDescription,
} from '@/components/ui/ayurveda-card';
import { Card } from '@/components/ui/card';
import {
  PageTransition,
  StaggerContainer,
  StaggerItem,
  FadeInUp,
  SlideInLeft,
  FloatingElement,
} from '@/components/ui/page-transition';
import {
  LotusIcon,
  LeafIcon,
  HerbIcon,
  WellnessDropIcon,
  PulseIcon,
  BalanceIcon,
} from '@/components/ui/ayurveda-icons';
import { Clock, MapPin, Phone, Award, Sparkles, ShieldCheck, HeartPulse, Users, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import ShirodharaImg from '@/assets/Shirodhara.png';
import NetraTarpanImg from '@/assets/NetraTarpan.png';
import JanuBastiImg from '@/assets/JanuBasti.png';
import KatBastiImg from '@/assets/KatBasti.png';
import HridBastiImg from '@/assets/HridBasti.png';

// Global scroll helper for Services section — robust with fallbacks and retries
const scrollToServicesTop = (behavior = 'auto') => {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('services');
  if (!el) return;

  const header = document.querySelector('header');
  const headerHeight = header ? header.offsetHeight : 0;
  const offset = headerHeight + 62; // header height + extra 62px gap
  const targetY = window.pageYOffset + el.getBoundingClientRect().top - offset;

  // Primary scroll attempt
  window.scrollTo({ top: targetY, behavior });

  // Fallback: element scrollIntoView then adjust by offset
  try {
    el.scrollIntoView({ behavior, block: 'start' });
    window.scrollBy(0, -offset);
  } catch (e) {
    // ignore
  }

  // Retry shortly after to handle layout shifts or sticky header transitions
  setTimeout(() => {
    window.scrollTo({ top: targetY, behavior: 'auto' });
    window.scrollBy(0, -offset);
  }, 60);
  setTimeout(() => {
    window.scrollTo({ top: targetY, behavior: 'auto' });
    window.scrollBy(0, -offset);
  }, 250);
};

// Gallery Section Component
const GallerySection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(false);

  const categories = [
    { id: 'All', label: 'All Images', icon: '🏥' },
    { id: 'Clinic Interior', label: 'Clinic & Facilities', icon: '🏢' },
    { id: 'Treatments', label: 'Treatments', icon: '💆‍♀️' },
    { id: 'Yoga & Wellness', label: 'Yoga & Wellness', icon: '🧘‍♀️' },
    { id: 'Education & Events', label: 'Education & Events', icon: '📚' },
    { id: 'Special Events', label: 'Special Events', icon: '🎉' }
  ];

  const galleryImages = [
    // Clinic Interior & Facilities
    {
      id: 1,
      src: '/ayur clinic images/clinic.jpeg',
      title: 'Main Clinic Building',
      category: 'Clinic Interior',
      description: 'Our modern Ayurvedic clinic with traditional architectural elements'
    },
    {
      id: 2,
      src: '/ayur clinic images/clinic (2).jpeg',
      title: 'Reception & Waiting Area',
      category: 'Clinic Interior',
      description: 'Welcoming reception area designed for patient comfort'
    },
    {
      id: 3,
      src: '/ayur clinic images/clinic (3).jpeg',
      title: 'Interior Design',
      category: 'Clinic Interior',
      description: 'Peaceful interior reflecting Ayurvedic healing principles'
    },
    {
      id: 4,
      src: '/ayur clinic images/clinic (5).jpeg',
      title: 'Treatment Areas',
      category: 'Clinic Interior',
      description: 'Clean and well-equipped treatment facilities'
    },
    
    // Treatment & Therapies
    {
      id: 5,
      src: '/ayur clinic images/treatment.jpeg',
      title: 'Panchakarma Treatment',
      category: 'Treatments',
      description: 'Traditional detoxification and rejuvenation therapies'
    },
    {
      id: 6,
      src: '/ayur clinic images/treatment (2).jpeg',
      title: 'Therapeutic Sessions',
      category: 'Treatments',
      description: 'Personalized treatment sessions for various health conditions'
    },
    
    // Yoga & Wellness Programs
    {
      id: 7,
      src: '/ayur clinic images/yoga (1).jpeg',
      title: 'Yoga Classes',
      category: 'Yoga & Wellness',
      description: 'Regular yoga sessions for physical and mental well-being'
    },
    {
      id: 8,
      src: '/ayur clinic images/yoga (2).jpeg',
      title: 'Group Yoga Sessions',
      category: 'Yoga & Wellness',
      description: 'Community yoga practices in our dedicated yoga hall'
    },
    {
      id: 9,
      src: '/ayur clinic images/yoga (3).jpeg',
      title: 'Wellness Programs',
      category: 'Yoga & Wellness',
      description: 'Comprehensive wellness programs combining yoga and Ayurveda'
    },
    {
      id: 10,
      src: '/ayur clinic images/yoga (4).jpeg',
      title: 'Meditation & Mindfulness',
      category: 'Yoga & Wellness',
      description: 'Guided meditation sessions for mental clarity'
    },
    {
      id: 11,
      src: '/ayur clinic images/yoga (5).jpeg',
      title: 'Therapeutic Yoga',
      category: 'Yoga & Wellness',
      description: 'Specialized yoga therapy for healing and recovery'
    },
    {
      id: 12,
      src: '/ayur clinic images/yoga (6).jpeg',
      title: 'Pranayama Practice',
      category: 'Yoga & Wellness',
      description: 'Breathing exercises for enhanced vitality and health'
    },
    
    // Educational Programs & Seminars
    {
      id: 13,
      src: '/ayur clinic images/seminar.jpeg',
      title: 'Health Seminars',
      category: 'Education & Events',
      description: 'Educational seminars on Ayurvedic health principles'
    },
    {
      id: 14,
      src: '/ayur clinic images/seminar (2).jpeg',
      title: 'Medical Conferences',
      category: 'Education & Events',
      description: 'Professional conferences and knowledge sharing sessions'
    },
    {
      id: 15,
      src: '/ayur clinic images/seminar (3).jpeg',
      title: 'Educational Workshops',
      category: 'Education & Events',
      description: 'Interactive workshops on traditional healing methods'
    },
    {
      id: 16,
      src: '/ayur clinic images/seminar (4).jpeg',
      title: 'Academic Discussions',
      category: 'Education & Events',
      description: 'Scholarly discussions on Ayurvedic medicine and research'
    },
    {
      id: 17,
      src: '/ayur clinic images/semiinar.jpeg',
      title: 'Community Seminars',
      category: 'Education & Events',
      description: 'Community outreach programs and health awareness sessions'
    },
    
    // Workshops & Training Programs
    {
      id: 18,
      src: '/ayur clinic images/workshop.jpeg',
      title: 'Practical Workshops',
      category: 'Education & Events',
      description: 'Hands-on training in Ayurvedic practices and techniques'
    },
    {
      id: 19,
      src: '/ayur clinic images/workshop (2).jpeg',
      title: 'Professional Training',
      category: 'Education & Events',
      description: 'Training programs for healthcare professionals'
    },
    {
      id: 20,
      src: '/ayur clinic images/workshop (3).jpeg',
      title: 'Skill Development',
      category: 'Education & Events',
      description: 'Skill development workshops in traditional healing arts'
    },
    {
      id: 21,
      src: '/ayur clinic images/workshop (4).jpeg',
      title: 'Interactive Learning',
      category: 'Education & Events',
      description: 'Interactive learning sessions with experienced practitioners'
    },
    {
      id: 22,
      src: '/ayur clinic images/workshop (5).jpeg',
      title: 'Certification Programs',
      category: 'Education & Events',
      description: 'Professional certification programs in Ayurvedic medicine'
    },
    
    // Special Events & Celebrations
    {
      id: 23,
      src: '/ayur clinic images/inauguration.jpeg',
      title: 'Clinic Inauguration',
      category: 'Special Events',
      description: 'Grand inauguration ceremony of our Ayurvedic clinic'
    },
    {
      id: 24,
      src: '/ayur clinic images/inauguration (1).jpeg',
      title: 'Opening Ceremony',
      category: 'Special Events',
      description: 'Traditional opening ceremony with distinguished guests'
    },
    {
      id: 25,
      src: '/ayur clinic images/inauguration (2).jpeg',
      title: 'Cultural Celebrations',
      category: 'Special Events',
      description: 'Cultural events celebrating Ayurvedic traditions'
    },
    {
      id: 26,
      src: '/ayur clinic images/inauguration (3).jpeg',
      title: 'Community Gathering',
      category: 'Special Events',
      description: 'Community gatherings and cultural celebrations'
    },
    {
      id: 27,
      src: '/ayur clinic images/flag hosting.jpeg',
      title: 'National Flag Hosting',
      category: 'Special Events',
      description: 'Patriotic celebrations and national flag hosting ceremonies'
    },
    {
      id: 28,
      src: '/ayur clinic images/flag_hosting.jpeg',
      title: 'Independence Day Celebration',
      category: 'Special Events',
      description: 'Independence Day celebrations at our clinic'
    },
    {
      id: 29,
      src: '/ayur clinic images/flag_hosting (2).jpeg',
      title: 'National Events',
      category: 'Special Events',
      description: 'Participation in national events and celebrations'
    },
    {
      id: 30,
      src: '/ayur clinic images/ayurvedic college student appreciation.jpeg',
      title: 'Student Appreciation',
      category: 'Special Events',
      description: 'Recognition and appreciation of Ayurvedic students'
    },
    {
      id: 31,
      src: '/ayur clinic images/ayurvedic college student appreciation (2).jpeg',
      title: 'Academic Excellence',
      category: 'Special Events',
      description: 'Celebrating academic excellence in Ayurvedic education'
    }
  ];

  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const handleCategoryChange = (category) => {
    setIsLoading(true);
    setActiveCategory(category);
    setTimeout(() => setIsLoading(false), 300);
  };

  const scrollLeft = () => {
    const container = document.getElementById('gallery-container');
    container.scrollBy({ left: -400, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = document.getElementById('gallery-container');
    container.scrollBy({ left: 400, behavior: 'smooth' });
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-ayurveda-beige-50 via-white to-ayurveda-sage-50">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header */}
        <FadeInUp className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ayurveda-sage-200 bg-white/70 backdrop-blur text-ayurveda-sage-700 text-sm font-medium mb-6">
            <div className="w-2 h-2 rounded-full bg-ayurveda-herbal-500"></div>
            <span>Clinic Gallery</span>
          </div>
          <h2 className="ayurveda-heading text-3xl md:text-5xl font-bold mb-4 text-ayurveda-sage-900">
            Our Healing Environment
          </h2>
          <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-2xl mx-auto leading-relaxed">
            A peaceful space designed for healing, wellness, and natural care with modern facilities and traditional ambiance.
          </p>
        </FadeInUp>

        {/* Enhanced Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-ayurveda-sage-500 to-ayurveda-herbal-500 text-white shadow-lg shadow-ayurveda-sage-500/25'
                  : 'bg-white border border-ayurveda-sage-200 text-ayurveda-sage-700 hover:bg-ayurveda-sage-50 hover:shadow-md'
              }`}
            >
              <span className="text-lg">{category.icon}</span>
              <span className="hidden sm:inline">{category.label}</span>
              <span className="sm:hidden">{category.label.split(' ')[0]}</span>
              {activeCategory === category.id && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-2 h-2 rounded-full bg-white"
                />
              )}
            </motion.button>
          ))}
        </div>

        {/* Image Count and Loading */}
        <div className="text-center mb-6">
          {isLoading ? (
            <div className="inline-flex items-center gap-2 text-ayurveda-sage-600">
              <div className="w-4 h-4 border-2 border-ayurveda-sage-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Loading images...</span>
            </div>
          ) : (
            <p className="text-sm text-ayurveda-sage-600">
              Showing <span className="font-semibold text-ayurveda-sage-800">{filteredImages.length}</span> images
              {activeCategory !== 'All' && <span> in {activeCategory}</span>}
            </p>
          )}
        </div>

        {/* Enhanced Gallery Container */}
        <div className="relative group">
          {/* Enhanced Scroll Arrows */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={scrollLeft}
            className="absolute left-0 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-white/95 backdrop-blur-md border border-ayurveda-sage-200 rounded-full w-12 h-12 flex items-center justify-center shadow-xl hover:bg-white hover:shadow-2xl transition-all duration-300 opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="h-6 w-6 text-ayurveda-sage-700" />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={scrollRight}
            className="absolute right-0 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-white/95 backdrop-blur-md border border-ayurveda-sage-200 rounded-full w-12 h-12 flex items-center justify-center shadow-xl hover:bg-white hover:shadow-2xl transition-all duration-300 opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="h-6 w-6 text-ayurveda-sage-700" />
          </motion.button>

          {/* Enhanced Scrollable Images Container */}
          <div
            id="gallery-container"
            className="flex gap-6 overflow-x-auto scroll-smooth pb-6 pt-2 px-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {filteredImages.map((image, index) => (
              <motion.div
                key={`${activeCategory}-${image.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex-shrink-0 group/card cursor-pointer"
              >
                <div className="relative w-80 md:w-96 h-72 md:h-80 rounded-3xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 border border-ayurveda-sage-100">
                  {/* Enhanced Category Badge */}
                  <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-gradient-to-r from-white/90 to-white/95 backdrop-blur-sm rounded-xl text-xs font-semibold text-ayurveda-sage-700 border border-ayurveda-sage-200/50 shadow-sm">
                    {image.category}
                  </div>

                  {/* Image with enhanced hover effect */}
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />

                  {/* Enhanced Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover/card:opacity-80 transition-opacity duration-300" />
                  
                  {/* Enhanced Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover/card:translate-y-0 transition-transform duration-300">
                    <h3 style={{ color: '#ffffff' }} className="text-xl font-bold ayurveda-heading mb-2 line-clamp-1 text-white">
                      {image.title}
                    </h3>
                    <p className="text-sm text-white line-clamp-2 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 delay-100">
                      {image.description}
                    </p>
                  </div>

                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover/card:border-ayurveda-herbal-400/50 transition-colors duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Enhanced CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-8 rounded-3xl border border-ayurveda-sage-200 bg-gradient-to-br from-white via-ayurveda-sage-25 to-ayurveda-beige-50 shadow-xl">
            <div className="text-center sm:text-left">
              <h3 className="text-xl font-bold text-ayurveda-sage-900 ayurveda-heading mb-2">
                Experience Our Healing Environment
              </h3>
              <p className="text-ayurveda-sage-600 max-w-md">
                Visit us for a personalized consultation in our serene, modern facility designed for your comfort and healing.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <AyurvedaButton size="lg" variant="primary" asChild>
                <a href="https://cal.com/sanjeevani-ayurved-wswdsi">Schedule Visit</a>
              </AyurvedaButton>
              <AyurvedaButton size="lg" variant="outline" asChild>
                <a href="#contact">Get Directions</a>
              </AyurvedaButton>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Available Services Component with Pagination
const AvailableServices = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const services = [
    'त्वचा रोग व्याधियों में किटिम, कील, मुहांसे, चेहरे की झाइयों के लिए वमन विरेचन, जलौकावचरण तथा लेपन कर्म, स्वर्ण शलाका द्वारा अग्नि कर्म किया जाता है।',
    'सभी साध्य बीमारियों का उपचार शमन चिकित्सा (दवाओं द्वारा किया जाता है।)',
    'असह्य दर्द/शूल में अग्नि कर्म, वेधन कर्म, रक्त मोक्षण द्वारा इलाज किया जाता है।',
    'बालकों के लिए स्वर्णप्राशन द्वारा रोग प्रतिरोधक क्षमता में वृद्धि एवं प्रकृति परीक्षण द्वारा भविष्य में बच्चा किस क्षेत्र में अच्छा कार्य करेगा, क्षेत्र निर्धारण किया जाता है।',
    'जीवन शैली जन्य बीमारियों, मधुमेह, उच्च रक्तचाप, थायराइड आदि के लिए प्रकृति परीक्षण द्वारा उपयुक्त आहार-विहार, जीवन शैली सुधार के लिए सुझाव, औषधियों द्वारा उपचार किया जाता है।',
    'कष्ट साध्य एवं असाध्य बीमारियों के उपद्रव के लिए शोधन चिकित्सा (वमन, विरेचन, बस्ति, शिरोधारा तथा रक्त मोक्षण) एवं शमन चिकित्सा (औषधियों द्वारा) इलाज किया जाता है।',
    'स्त्री रोगों के लिए आयुर्वेदिक एवं प्राकृतिक उपचार।',
    'गर्भावस्था में होने वाले उपद्रवों का आयुर्वेदिक उपचार।',
    'मांसानुमांसिक विकास के लिए आहार एवं औषधियों का प्रकृति अनुरूप सलाह।',
    'एकांगवात, अर्धवात, कटिशूल, ग्रीवास्तंभ में स्थानीय एवं शोधन तथा बृहण बस्ति द्वारा इलाज।',
    'वंध्यत्व निवारण हेतु आयुर्वेदिक निदान के माध्यम से औषधियों तथा शोधन चिकित्सा द्वारा गर्भाधान की सलाह दी जाती है।',
    'भविष्य में होने वाली बीमारियों के लिए माता-पिता को बच्चे के स्वास्थ्य के लिए जागरूक करना।',
    'बवासीर, परिकर्तिका का उपचार क्षार सूत्र के द्वारा किया जाता है।'
  ];

  const totalPages = Math.ceil(services.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = services.slice(startIndex, endIndex);

  // Use the global scroll helper defined at module scope so all callers share the same robust behavior.

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const next = currentPage + 1;
      setCurrentPage(next);
      scrollToServicesTop();
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prev = currentPage - 1;
      setCurrentPage(prev);
      scrollToServicesTop();
    }
  };

  const handleSetPage = (page) => {
    // update page only if different, but always scroll so clicking the same page still navigates to the section
    if (page !== currentPage) {
      setCurrentPage(page);
    }
    scrollToServicesTop();
  };

  return (
    <>
      <StaggerContainer className="max-w-5xl mx-auto space-y-4">
        {currentServices.map((service, index) => (
          <StaggerItem key={startIndex + index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ x: 5 }}
              className="flex items-start gap-4 p-5 rounded-2xl border border-ayurveda-sage-200 bg-white/70 backdrop-blur shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex-shrink-0 mt-1">
                <div className="h-2 w-2 rounded-full bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500" />
              </div>
              <p className="text-ayurveda-sage-700 leading-relaxed text-base">
                {service}
              </p>
            </motion.div>
          </StaggerItem>
        ))}
      </StaggerContainer>

      {/* Pagination Controls */}
      <div className="mt-8 flex items-center justify-center gap-4">
        <AyurvedaButton
          variant="outline"
          size="sm"
          onClick={currentPage === 1 ? undefined : handlePrevPage}
          disabled={currentPage === 1}
          aria-disabled={currentPage === 1}
          tabIndex={currentPage === 1 ? -1 : 0}
          className={`disabled:opacity-50 disabled:cursor-not-allowed ${currentPage === 1 ? 'pointer-events-none' : ''}`}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          पिछला
        </AyurvedaButton>

        <div className="flex items-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handleSetPage(page)}
              className={`h-10 w-10 rounded-xl font-medium transition-all ${
                currentPage === page
                  ? 'bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 text-white shadow-md'
                  : 'bg-white border border-ayurveda-sage-200 text-ayurveda-sage-700 hover:bg-ayurveda-sage-50'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        <AyurvedaButton
          variant="outline"
          size="sm"
          onClick={currentPage === totalPages ? undefined : handleNextPage}
          disabled={currentPage === totalPages}
          aria-disabled={currentPage === totalPages}
          tabIndex={currentPage === totalPages ? -1 : 0}
          className={`disabled:opacity-50 disabled:cursor-not-allowed ${currentPage === totalPages ? 'pointer-events-none' : ''}`}
        >
          अगला
          <ChevronRight className="h-4 w-4 ml-1" />
        </AyurvedaButton>
      </div>

      <div className="mt-8 text-center">
        <AyurvedaButton size="lg" variant="primary" asChild>
          <a href="https://cal.com/sanjeevani-ayurved-wswdsi"> Appointment बुक करें</a>
        </AyurvedaButton>
      </div>
    </>
  );
};

// Navbar Component
const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Treatments', href: '#treatments' },
    { label: 'विशेष चिकित्सा', href: '#special-therapies' },
    { label: 'Services', href: '#services' },
    { label: 'About Us', href: '#about' },
    { label: 'Contact Us', href: '#contact' },
  ];

  return (
    <motion.header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-ayurveda-sage-200 shadow-sm"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="container mx-auto px-3 sm:px-4 md:px-6">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <motion.a 
            href="#home" 
            className="flex items-center gap-2 sm:gap-3"
            whileHover={{ scale: 1.02 }}
          >
            <img 
              src="/logo.png" 
              alt="Sanjivani Ayurvedic Clinic" 
              className="h-12 sm:h-14 w-auto object-contain" 
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.href}
                onClick={(e) => {
                  if (link.href === '#services') {
                    e.preventDefault();
                    scrollToServicesTop();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-ayurveda-sage-700 hover:text-ayurveda-herbal-600 hover:bg-ayurveda-sage-50 rounded-xl transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {link.label}
              </motion.a>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <AyurvedaButton variant="primary" size="sm" asChild>
              <a href="https://cal.com/sanjeevani-ayurved-wswdsi">अपॉइंटमेंट बुक करें</a>
            </AyurvedaButton>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl hover:bg-ayurveda-sage-50 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-ayurveda-sage-700" />
            ) : (
              <Menu className="h-6 w-6 text-ayurveda-sage-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
            >
              <nav className="py-4 space-y-2">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={index}
                    href={link.href}
                    onClick={(e) => {
                      if (link.href === '#services') {
                        e.preventDefault();
                        scrollToServicesTop();
                      }
                      setMobileMenuOpen(false);
                    }}
                    className="block px-4 py-3 text-sm font-medium text-ayurveda-sage-700 hover:text-ayurveda-herbal-600 hover:bg-ayurveda-sage-50 rounded-xl transition-all"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {link.label}
                  </motion.a>
                ))}
                <div className="pt-2">
                  <AyurvedaButton variant="primary" size="sm" className="w-full" asChild>
                    <a href="https://cal.com/sanjeevani-ayurved-wswdsi" onClick={() => setMobileMenuOpen(false)}>
                      अपॉइंटमेंट बुक करें
                    </a>
                  </AyurvedaButton>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export const LandingPage = () => {
  // Handle smooth scroll with offset for sticky header

  const treatments = [
    {
      icon: WellnessDropIcon,
      title: 'Panchakarma Therapy',
      description:
        'Detoxification and rejuvenation through traditional five-action cleansing that purifies body and mind.',
    },
    {
      icon: PulseIcon,
      title: 'Chronic Disease Management',
      description:
        'Holistic care for diabetes, arthritis, digestive and metabolic disorders using classical Ayurvedic protocols.',
    },
    {
      icon: BalanceIcon,
      title: 'Lifestyle Consultation',
      description:
        'Personalized guidance on diet, yoga and dinacharya aligned with your unique prakriti (constitution).',
    },
  ];

  const trust = [
    { icon: ShieldCheck, label: 'Certified Ayurvedic Practice' },
    { icon: HeartPulse, label: 'Root-cause Healing Approach' },
    { icon: Users, label: 'Thousands of Consultations' },
  ];

  return (
    <PageTransition className="relative bg-white">
      {/* Subtle background texture */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-ayurveda-sage-100 blur-3xl opacity-60" />
        <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-ayurveda-gold-100 blur-3xl opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(16,88,61,0.06)_1px,transparent_0)] [background-size:22px_22px] opacity-60" />
      </div>

      {/* Navigation */}
      <Navbar />

      {/* Hero */}
      <section id="home" className="relative pt-14 md:pt-20 lg:pt-24 pb-12 md:pb-20">
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <StaggerContainer className="space-y-7">
                <StaggerItem>
                  <motion.div
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-ayurveda-gold-300 bg-white/70 backdrop-blur text-ayurveda-sage-800 text-sm font-medium shadow-sm"
                    whileHover={{ scale: 1.03 }}
                  >
                    <Sparkles className="h-4 w-4 text-ayurveda-gold-500" />
                    <span>Restore Balance. <span className="text-ayurveda-herbal-600">Feel Naturally</span> <span className="text-ayurveda-gold-600">Well.</span></span>
                  </motion.div>
                </StaggerItem>

                <StaggerItem>
                  <h1 className="ayurveda-heading text-4xl md:text-5xl lg:text-6xl font-bold text-ayurveda-sage-900 leading-[1.08] tracking-tight">
                    A center for complete <span className="text-ayurveda-herbal-600 font-semibold">ayurvedic treatment</span>, <span className="text-ayurveda-gold-500 font-semibold">research and development</span>
                  </h1>
                </StaggerItem>

                <StaggerItem>
                  <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-2xl leading-relaxed">
                    Authentic Ayurvedic care with personalized treatments that address the root cause — harmonizing mind,
                    body, and spirit through classical wisdom and modern assessment.
                  </p>
                </StaggerItem>

                <StaggerItem>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <AyurvedaButton size="xl" variant="primary" asChild>
                      <a href="https://cal.com/sanjeevani-ayurved-wswdsi">Book an Appointment</a>
                    </AyurvedaButton>
                    <AyurvedaButton size="xl" variant="secondary" asChild>
                      <a href="#treatments">View Treatments</a>
                    </AyurvedaButton>
                  </div>
                </StaggerItem>

                <StaggerItem>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4">
                    {trust.map((t, i) => {
                      const Icon = t.icon;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-3 rounded-2xl border border-ayurveda-sage-200 bg-white/70 backdrop-blur px-4 py-3 shadow-sm"
                        >
                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 flex items-center justify-center shadow">
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <div className="text-sm font-medium text-ayurveda-sage-800 leading-snug">{t.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </StaggerItem>
              </StaggerContainer>
            </div>

            {/* Hero side card */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -inset-2 rounded-[28px] bg-gradient-to-br from-ayurveda-sage-200 via-white to-ayurveda-gold-200 blur-xl opacity-70" />
                <AyurvedaCard className="relative overflow-hidden rounded-[28px] border border-ayurveda-sage-200 shadow-2xl">
                  <div className="p-6 md:p-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-ayurveda-sage-500 font-medium">Today’s Highlights</div>
                        <div className="ayurveda-heading text-xl font-semibold text-ayurveda-sage-900">
                          Personalized Consultation
                        </div>
                      </div>
                      <div className="rounded-2xl bg-ayurveda-gold-50 border border-ayurveda-gold-200 px-3 py-1 text-xs font-semibold text-ayurveda-gold-700">
                        New Patient
                      </div>
                    </div>

                    <div className="mt-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-ayurveda-sage-50 border border-ayurveda-sage-200 flex items-center justify-center">
                          <Clock className="h-5 w-5 text-ayurveda-sage-700" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ayurveda-sage-800">Clinic Timings</div>
                          <div className="text-sm text-ayurveda-sage-600">Mon–Sat: 10 AM–2 PM & 6 PM–9 PM</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-ayurveda-sage-50 border border-ayurveda-sage-200 flex items-center justify-center">
                          <MapPin className="h-5 w-5 text-ayurveda-sage-700" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ayurveda-sage-800">Location</div>
                          <div className="text-sm text-ayurveda-sage-600">Gomti Nagar, Lucknow</div>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-ayurveda-sage-50 border border-ayurveda-sage-200 flex items-center justify-center">
                          <Phone className="h-5 w-5 text-ayurveda-sage-700" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ayurveda-sage-800">Call Us</div>
                          <div className="text-sm text-ayurveda-sage-600">0522-4621328</div>
                          <div className="text-sm text-ayurveda-sage-600">+91 9839611010</div>
                          <div className="text-sm text-ayurveda-sage-600">+91 7081076983</div>
                          <div className="text-sm text-ayurveda-sage-600">+91 9918391600</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-7">
                      <AyurvedaButton
  size="lg"
  variant="primary"
  className="w-full whitespace-nowrap"
  asChild
>
  <a href="#contact" className="text-sm sm:text-base md:text-lg">
    Get Directions & Contact
  </a>
</AyurvedaButton>

                    </div>
                  </div>

                  {/* subtle bottom accent */}
                  <div className="h-2 w-full bg-gradient-to-r from-ayurveda-sage-500 via-ayurveda-herbal-500 to-ayurveda-gold-500" />
                </AyurvedaCard>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Floating decorative icons (more subtle) */}
        <FloatingElement className="absolute top-24 left-10 text-ayurveda-sage-200 hidden lg:block opacity-70">
          <LeafIcon className="w-14 h-14" />
        </FloatingElement>
        <FloatingElement className="absolute top-28 right-16 text-ayurveda-herbal-200 hidden lg:block opacity-70">
          <HerbIcon className="w-12 h-12" />
        </FloatingElement>
        <FloatingElement className="absolute bottom-14 left-1/4 text-ayurveda-gold-200 hidden lg:block opacity-60">
          <LotusIcon className="w-16 h-16" />
        </FloatingElement>
      </section>

      {/* Special Therapies Section */}
      <section id="special-therapies" className="py-14 md:py-20 bg-white scroll-mt-16 sm:scroll-mt-20">
        <div className="container mx-auto px-4 md:px-6">
          <FadeInUp className="text-center mb-12">
            <h2 className="ayurveda-heading text-3xl md:text-5xl font-bold mb-4 text-ayurveda-sage-900">
              विशेष चिकित्सा कर्म
            </h2>
            <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-3xl mx-auto leading-relaxed">
              व्याधि अनुसार अलग-अलग विशेष कर्म भी किये जाते हैं
            </p>
            <p className="text-sm md:text-base text-ayurveda-sage-500 max-w-3xl mx-auto mt-2">
              According to diseases, different special therapies are performed
            </p>
          </FadeInUp>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                title: 'शिरोधारा',
                titleEn: 'Shirodhara',
                description: 'शिर:शूल, स्मृतिनाश, क्रोध, तनाव, उत्तेजना आदि मानसिक रोगों में उपयोगी',
                descriptionEn: 'Useful in headache, memory loss, anger, stress, excitement, and other mental disorders',
                image: ShirodharaImg,
                gradient: 'from-ayurveda-sage-500 to-ayurveda-herbal-500'
              },
              {
                title: 'नेत्र तर्पण',
                titleEn: 'Netra Tarpana',
                description: 'नेत्र शुष्कता, दृष्टिदोष, प्रोग्रेसिव मायोपिया आदि में उपयोगी',
                descriptionEn: 'Useful for dry eyes, vision defects, progressive myopia, etc.',
                image: NetraTarpanImg,
                gradient: 'from-ayurveda-herbal-500 to-ayurveda-gold-500'
              },
              {
                title: 'जानु बस्ति',
                titleEn: 'Janu Basti',
                description: 'घुटनों के दर्द में (गठिया) में उपयोगी',
                descriptionEn: 'Useful for knee pain, arthritis',
                image: JanuBastiImg,
                gradient: 'from-ayurveda-gold-500 to-ayurveda-sandalwood-500'
              },
              {
                title: 'कटि बस्ति',
                titleEn: 'Kati Basti',
                description: 'कमर दर्द (बैक पेन) में उपयोगी',
                descriptionEn: 'Useful for lower back pain',
                image: KatBastiImg,
                gradient: 'from-ayurveda-sage-500 to-ayurveda-gold-500'
              },
              {
                title: 'हृद बस्ति',
                titleEn: 'Hrid Basti',
                description: 'सीने के दर्द और धड़कन में उपयोगी',
                descriptionEn: 'Useful for chest pain and palpitations',
                image: HridBastiImg,
                gradient: 'from-ayurveda-herbal-500 to-ayurveda-sage-500'
              }
            ].map((therapy, index) => (
              <StaggerItem key={index}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="h-full"
                >
                  <AyurvedaCard className="h-full overflow-hidden rounded-[24px] border border-ayurveda-sage-200 shadow-sm hover:shadow-2xl transition-all duration-300">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={therapy.image} 
                        alt={therapy.title}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${therapy.gradient} opacity-40`} />
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                        <h3 style={{ color: '#ffffff' }} className="text-white font-bold text-xl ayurveda-heading">{therapy.title}</h3>
                        <p className="text-white text-sm font-medium">{therapy.titleEn}</p>
                      </div>
                    </div>

                    {/* Content */}
                    <AyurvedaCardContent className="p-6">
                      <div className="space-y-3">
                        <p className="text-ayurveda-sage-700 leading-relaxed font-medium">
                          {therapy.description}
                        </p>
                        <p className="text-ayurveda-sage-500 text-sm leading-relaxed italic">
                          {therapy.descriptionEn}
                        </p>
                      </div>

                      <div className="mt-5">
                        <AyurvedaButton size="sm" variant="outline" className="w-full" asChild>
                          <a href="https://cal.com/sanjeevani-ayurved-wswdsi">जानकारी के लिए संपर्क करें</a>
                        </AyurvedaButton>
                      </div>
                    </AyurvedaCardContent>
                  </AyurvedaCard>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 p-6 rounded-2xl border border-ayurveda-sage-200 bg-gradient-to-br from-ayurveda-beige-50 to-ayurveda-sage-50">
              <div className="text-center sm:text-left">
                <p className="text-lg font-semibold text-ayurveda-sage-900 ayurveda-heading">
                  अपनी समस्या के अनुसार उपचार जानें
                </p>
                <p className="text-sm text-ayurveda-sage-600">
                  Know the treatment according to your problem
                </p>
              </div>
              <AyurvedaButton size="lg" variant="primary" asChild>
                <a href="https://cal.com/sanjeevani-ayurved-wswdsi">परामर्श बुक करें</a>
              </AyurvedaButton>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <GallerySection />

      {/* Available Services - Detailed */}
      <section id="services" className="py-14 md:py-20 bg-gradient-to-br from-ayurveda-beige-50 via-white to-ayurveda-sage-50">
        <div className="container mx-auto px-4 md:px-6">
          <FadeInUp className="text-center mb-12">
            <h2 className="ayurveda-heading text-3xl md:text-5xl font-bold mb-4 text-ayurveda-sage-900">
              उपलब्ध सेवाएं
            </h2>
            <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-3xl mx-auto leading-relaxed">
              यहाँ पर प्रत्येक प्रकार की बीमारियों का आयुर्वेदिक पद्धति द्वारा उपचार किया जाता है
            </p>
          </FadeInUp>

          <AvailableServices />
        </div>
      </section>

      {/* About Doctor */}
      <section id="about" className="py-14 md:py-20 bg-gradient-to-r from-ayurveda-beige-50 to-ayurveda-sage-50">
        <div className="container mx-auto px-4 md:px-6">
          <FadeInUp className="text-center mb-12">
            <h2 className="ayurveda-heading text-3xl md:text-5xl font-bold mb-4 text-ayurveda-sage-900">
              हमारे विशेषज्ञ चिकित्सक
            </h2>
            <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-2xl mx-auto">
              Our Expert Ayurvedic Physicians
            </p>
          </FadeInUp>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Dr. B.P. Singh */}
            <StaggerItem>
              <AyurvedaCard className="h-full">
                <AyurvedaCardContent className="p-8 lg:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-gradient-to-br from-ayurveda-sage-500 to-ayurveda-herbal-500 w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                      <Award className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="ayurveda-heading text-2xl font-bold text-ayurveda-sage-900">Dr. B.P. Singh</h3>
                      <p className="text-ayurveda-sage-600 font-medium">B.A.M.S (Ayurveda)</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 text-ayurveda-sage-700 leading-relaxed">
                    <p>
                      With over 25 years of clinical experience in Ayurvedic medicine, Dr. Singh specializes in chronic disease management, Panchakarma therapy, and lifestyle medicine.
                    </p>
                    <p>
                      He completed his Bachelor of Ayurvedic Medicine and Surgery (B.A.M.S) from the prestigious Government Ayurvedic College Gurukul Kangri, Haridwar.
                    </p>
                    <div className="pt-4 border-t border-ayurveda-sage-200">
                      <p className="font-medium text-ayurveda-sage-900 italic">
                        "My approach combines time-tested Ayurvedic wisdom with modern diagnostic methods to create truly personalized treatment plans."
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-ayurveda-sage-200">
                    <div className="flex items-center gap-2 text-sm text-ayurveda-sage-600">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">Consultation Timing:</span>
                    </div>
                    <p className="text-ayurveda-sage-700 mt-2">
                      Mon-Sat: 10:00 AM - 2:00 PM & 6:00 PM - 9:00 PM <br />
                      Sunday: 10:00 AM - 2:00 PM & Evening Closed 
                    </p>
                  </div>
                </AyurvedaCardContent>
              </AyurvedaCard>
            </StaggerItem>

            {/* Dr. Shweta Singh */}
            <StaggerItem>
              <AyurvedaCard className="h-full">
                <AyurvedaCardContent className="p-8 lg:p-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-gradient-to-br from-ayurveda-gold-500 to-ayurveda-sandalwood-500 w-16 h-16 rounded-full flex items-center justify-center shadow-lg">
                      <Award className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="ayurveda-heading text-2xl font-bold text-ayurveda-sage-900">Dr. Shweta Singh</h3>
                      <p className="text-ayurveda-sage-600 font-medium text-sm">B.A.M.S, D.E.M.S, C.G.O, C.C.H</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4 text-ayurveda-sage-700 leading-relaxed">
                    <p>
                      Dr. Shweta Singh is a highly qualified Ayurvedic physician with specialized expertise in women's health, reproductive medicine, and holistic wellness.
                    </p>
                    
                    <div className="bg-ayurveda-sage-50 rounded-xl p-4 border border-ayurveda-sage-200">
                      <p className="font-semibold text-ayurveda-sage-900 mb-2">Areas of Expertise:</p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <span className="text-ayurveda-herbal-600 mt-1">•</span>
                          <span><strong>Infertility Treatment:</strong> Specialized Ayurvedic protocols for conception and reproductive health</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-ayurveda-herbal-600 mt-1">•</span>
                          <span><strong>Obesity Management:</strong> Natural weight management through Ayurvedic principles</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-ayurveda-herbal-600 mt-1">•</span>
                          <span><strong>Women's Health:</strong> Comprehensive care for gynecological concerns</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-ayurveda-sage-200">
                    <div className="flex items-center gap-2 text-sm text-ayurveda-sage-600">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">Consultation Timing:</span>
                    </div>
                    <p className="text-ayurveda-sage-700 mt-2">
                      Monday to Friday: 11:00 AM - 1:00 PM
                    </p>
                  </div>
                </AyurvedaCardContent>
              </AyurvedaCard>
            </StaggerItem>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <AyurvedaButton size="lg" variant="primary" asChild>
              <a href="https://cal.com/sanjeevani-ayurved-wswdsi">अपॉइंटमेंट बुक करें</a>
            </AyurvedaButton>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-14 md:py-20 bg-white scroll-mt-16 sm:scroll-mt-20">
        <div className="container mx-auto px-4 md:px-6">
          <FadeInUp className="max-w-5xl mx-auto">

            <div className="text-center mb-12">
              <h2 className="ayurveda-heading text-3xl md:text-5xl font-bold mb-4 text-ayurveda-sage-900">
                Visit Our Center
              </h2>
              <p className="text-base md:text-lg text-ayurveda-sage-600 max-w-2xl mx-auto">
                Experience authentic Ayurvedic healing in a calm, nurturing environment.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6 lg:p-8 bg-card border border-border">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Address</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      3A/12, Vishal Khand - 3, Vishal Khand, Gomti Nagar<br />Lucknow, Uttar Pradesh 226010
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 lg:p-8 bg-card border border-border">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Contact</h3>
                    <div className="text-muted-foreground leading-relaxed">
                      <div>
                        <span className="font-medium">Phone:</span>
                        <div className="mt-1 space-y-1">
                          <a href="tel:05224621328" className="block hover:text-ayurveda-herbal-600 transition-colors">0522-4621328, +91 9839611010,<br /> +91 7081076983, +91 9918391600</a>
                        </div>
                      </div>

                      <div className="mt-3">
                        <span className="font-medium">Email:</span>
                        <div className="mt-1">
                          <a href="mailto:sanjeevani226010@gmail.com" className="hover:text-ayurveda-herbal-600 transition-colors">sanjeevani226010@gmail.com</a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 lg:p-8 bg-card border border-border md:col-span-2">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-3">Clinic Timings</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-muted-foreground">
                      <div>
                        <span className="font-medium text-foreground">Monday - Saturday: Morning</span> 10:00 AM - 2:00 PM
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Evening:</span> 6:00 PM - 9:00 PM
                      </div>
                      <div >
                        <span className="font-medium text-foreground">Sunday: Morning </span>10:00 AM - 2:00 PM
                      </div>
                       <div>
                        <span className="font-medium text-foreground">Evening:</span> Closed
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ayurveda-sage-200 bg-white">
        <div className="container mx-auto px-4 md:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Sanjivani Ayurvedic Clinic" className="h-20 w-auto object-contain" />
              </div>
              <p className="text-sm text-ayurveda-sage-600 leading-relaxed">
                Authentic Ayurvedic healing with personalized treatments that restore balance to mind, body, and spirit.
              </p>
            </div>
            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="ayurveda-heading text-base font-semibold text-ayurveda-sage-900">Contact Us</h3>
              <div className="space-y-3 text-sm text-ayurveda-sage-600">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-ayurveda-sage-500 flex-shrink-0" />
                  <span>3A/12, Vishal Khand - 3, Gomti Nagar, Lucknow, UP 226010</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <a href="tel:05224621328" className="hover:text-ayurveda-herbal-600 transition-colors">0522-4621328</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <a href="tel:+919839611010" className="hover:text-ayurveda-herbal-600 transition-colors">+91 9839611010</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <a href="tel:+917081076983" className="hover:text-ayurveda-herbal-600 transition-colors">+91 7081076983</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <a href="tel:+919918391600" className="hover:text-ayurveda-herbal-600 transition-colors">+91 9918391600</a>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <span>Mon-Sat: 9 AM - 3 PM & 6 PM - 9 PM</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-ayurveda-sage-500 flex-shrink-0" />
                  <span>Sun: 9 AM - 3 PM & Closed</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="ayurveda-heading text-base font-semibold text-ayurveda-sage-900">Quick Links</h3>
              <div className="space-y-2 text-sm">
                <a href="#treatments" className="block text-ayurveda-sage-600 hover:text-ayurveda-herbal-600 transition-colors">Treatments & Services</a>
                <a href="#contact" className="block text-ayurveda-sage-600 hover:text-ayurveda-herbal-600 transition-colors">Visit Clinic</a>
                <a href="https://cal.com/sanjeevani-ayurved-wswdsi" target="_blank" rel="noopener noreferrer" className="block text-ayurveda-sage-600 hover:text-ayurveda-herbal-600 transition-colors">Book Appointment</a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-ayurveda-sage-200 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ayurveda-sage-500">
            <p>© 2024 Sanjivani Ayurvedic Center. All rights reserved.</p>
            <p>
              Crafted by{' '}
              <a 
                href="https://www.shivedait.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-ayurveda-sage-700 hover:text-ayurveda-herbal-600 transition-colors underline"
              >
                Shiveda IT Services
              </a>
            </p>
          </div>
        </div>
      </footer>
    </PageTransition>
  );
};

export default LandingPage;
